import React, { useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { graphql } from '../../gql'
import { useGQLInfinite } from '../../hooks/GQLUtilityHooks'
import { Button } from '../../components/shadcn/Button'
import { DisposalCreateTable } from './DisposalCreateTable'
import { Loader } from '../../components/Loader'
import { Combox } from '../../components/shadcn/combox'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowLeft,
  faBuilding,
  faCheck,
  faExclamationTriangle,
  faSearch,
  faTruck,
  faXmark,
  faLayerGroup,
} from '@fortawesome/pro-solid-svg-icons'
import { useDisposalCreate } from '../../lib/hooks/useDisposalCreate'
import { CarrierSelector } from '../../components/CarrierSelector'
import { packageFilters } from '../../gql/graphql'

// GraphQL: list packages eligible for disposal
const PackagesForDisposalQuery = graphql(`
  query PackagesForDisposal($page: Int!, $filters: packageFilters, $sort: String, $direction: String) {
    packages(page: $page, filters: $filters, sort: $sort, direction: $direction) {
      items {
        id
        order {
          carrier {
            id
            name
          }
        }
        ...DisposalCreateTable_PackageFragment
      }
      paginator {
        current
        endPage
        totalCount
      }
    }
  }
`)

const ErrorState: React.FC<{ error: any }> = ({ error }) => (
  <div className="p-8 text-center text-gray-500 w-[1400px] flex flex-col items-center justify-center min-h-[400px]">
    <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-red-500 mb-4" />
    <div className="text-lg font-medium mb-2 text-gray-800">Chyba při načítání</div>
    <div className="text-sm text-gray-600">{error?.message || 'Neznámá chyba'}</div>
  </div>
)

const LoadingState: React.FC = () => (
  <div className="p-8">
    <Loader size="fill-container" />
  </div>
)

const EmptyState: React.FC = () => (
  <div className="p-8 text-center text-gray-500 w-[1400px] flex flex-col items-center justify-center min-h-[400px]">
    <FontAwesomeIcon icon={faXmark} className="text-4xl text-red-500 mb-4" />
    <div className="text-lg font-medium mb-2">Žádné balíky k dispozici</div>
    <div className="text-sm">Vyberte firmu a dopravce pro zobrazení balíků připravených k svozu.</div>
  </div>
)

const LoadMoreButton: React.FC<{ onClick: () => void; isFetching: boolean }> = ({ onClick, isFetching }) => (
  <div className="p-4 border-t w-[1400px]">
    <Button variant={isFetching ? 'disabled' : 'outline'} className="w-full" disabled={isFetching} onClick={onClick}>
      {isFetching ? (
        <div className="flex items-center gap-2">
          <div className="loader-small"></div>
          Načítání...
        </div>
      ) : (
        'Načíst další'
      )}
    </Button>
  </div>
)

const BulkDisposalModal: React.FC<{
  isOpen: boolean
  isCreating: boolean
  progress: { current: number; total: number; currentGroup: string }
  result: { success: number; failed: number }
}> = ({ isOpen, isCreating, progress, result }) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl w-[500px]">
        <h2 className="text-xl font-bold mb-4">
          {isCreating ? 'Vytváření a odesílání hromadného svozu...' : 'Hotovo!'}
        </h2>

        <div className="mb-4">
          <div className="flex justify-between text-sm mb-2">
            <span>Postup:</span>
            <span className="font-semibold">
              {progress.current} / {progress.total}
            </span>
          </div>

          <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
            <div
              className="bg-blue-400 h-4 transition-all duration-300 flex items-center justify-center text-xs text-white font-medium"
              style={{ width: `${(progress.current / progress.total) * 100}%` }}>
              {progress.total > 0 ? `${Math.round((progress.current / progress.total) * 100)}%` : ''}
            </div>
          </div>
        </div>

        {progress.currentGroup && isCreating && (
          <div className="text-sm text-gray-600 mb-4">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faTruck} />
              <span>Firma – dopravce: {progress.currentGroup}</span>
            </div>
          </div>
        )}

        {!isCreating && result.failed === 0 && (
          <div className="text-center text-green-600 font-medium">
            <FontAwesomeIcon icon={faCheck} className="mr-2" />
            Svozy byly úspěšně vytvořeny a odeslány
          </div>
        )}

        {!isCreating && result.failed > 0 && (
          <div className="text-center text-red-600 font-medium">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
            Vytvořeno a odesláno: {result.success}, selhalo: {result.failed}
          </div>
        )}
      </div>
    </div>
  )
}

export const DisposalCreate: React.FC = () => {
  const history = useHistory()
  const queryClient = useQueryClient()

  const {
    selectedCompany,
    setSelectedCompany,
    selectedCarrier,
    setSelectedCarrier,
    selectedPackageIds,
    childCompanies,
    company,
    createDisposalMutation,
    sendDisposalMutation,
    isBulkModalOpen,
    bulkProgress,
    bulkResult,
    isBulkCreating,
    toggleAll,
    toggleOne,
    toggleRange,
    handleCreate,
    handleBulkCreate,
    resetFilters,
  } = useDisposalCreate()

  // Build filters for packages query
  const gqlFilters: Partial<packageFilters> = useMemo(() => {
    return {
      companies: selectedCompany ? [selectedCompany] : undefined,
      carriers: selectedCarrier.length ? selectedCarrier : undefined,
      isSent: true,
      hasDisposal: false,
      orderProcessStatuses: ['packed'],
    }
  }, [selectedCompany, selectedCarrier])

  // Fetch packages
  const {
    data: packages,
    isLoading,
    isFetching: isQueryFetching,
    fetchNextPage,
    hasNextPage,
    error,
  } = useGQLInfinite<any>(PackagesForDisposalQuery, ['packagesForDisposal'], {
    page: 1,
    filters: gqlFilters,
    sort: 'id',
    direction: 'DESC',
  })

  const refreshPackages = () => {
    queryClient.invalidateQueries({ queryKey: ['packagesForDisposal'] })
  }

  const isCreating = createDisposalMutation.isPending

  const renderMainContent = () => {
    if (error) return <ErrorState error={error} />
    if (isLoading) return <LoadingState />
    if (!packages || packages.length === 0) return <EmptyState />

    return (
      <>
        <DisposalCreateTable
          packages={packages}
          selectedIds={selectedPackageIds}
          onToggleAll={() => toggleAll(packages)}
          onToggleOne={toggleOne}
          onToggleRange={toggleRange}
          isDisabled={isCreating}
          showCarrier
          showCompany={!selectedCompany}
        />
        {hasNextPage && <LoadMoreButton onClick={fetchNextPage} isFetching={isQueryFetching} />}
      </>
    )
  }

  return (
    <div className="h-full">
      {/* Filters Section */}
      <div className="orders-filters bg-white p-4 flex justify-center">
        <div
          className="grid gap-2 items-end w-[1400px]"
          style={{ gridTemplateColumns: '60px 1fr 1fr 1fr 1fr 1fr 1fr' }}>
          {/* Back Button */}
          <Button
            onClick={() => history.push('/disposal')}
            variant="outline"
            size="lg"
            title="Zpět na seznam"
            icon={faArrowLeft}
          />
          {/* Company Filter */}
          {childCompanies.length > 0 && (
            <div className="form-control gap-1">
              <label className="flex items-center gap-2">
                <FontAwesomeIcon icon={faBuilding} /> Firma
              </label>
              <Combox
                items={[
                  { value: null, label: 'Vše' },
                  ...childCompanies.map(c => ({ value: String(c.id), label: c.name })),
                ]}
                placeholder="Vyberte firmu"
                value={selectedCompany ? String(selectedCompany) : undefined}
                onValueChange={value => setSelectedCompany(value ? Number(value) : undefined)}
              />
            </div>
          )}

          {/* Carrier Filter */}
          <div className="form-control gap-1">
            <label className="flex items-center gap-2">
              <FontAwesomeIcon icon={faTruck} /> Dopravce
            </label>
            <CarrierSelector
              mode="multi"
              value={selectedCarrier}
              onValueChange={setSelectedCarrier}
              placeholder="Vše"
            />
          </div>

          {/* Action Buttons */}
          <div className="col-span-3 flex items-start gap-2">
            <Button onClick={resetFilters} variant="outlineDanger" size="lg" title="Vymazat filtry" icon={faXmark} />
            <Button
              onClick={refreshPackages}
              variant="default"
              size="lg"
              title="Vyhledat"
              icon={faSearch}
              className="w-48">
              Vyhledat
            </Button>
          </div>

          {childCompanies.length === 0 && <div className="col-span-1 flex justify-end" />}

          {/* Create Button */}
          <div className="col-span-1 flex justify-end gap-2">
            {selectedCarrier.length === 1 && selectedCompany ? (
              <Button
                variant="default"
                size="lg"
                className="bg-green w-48"
                icon={faCheck}
                onClick={handleCreate}
                disabled={isQueryFetching || isLoading || isCreating || selectedPackageIds.length === 0}
                title={selectedPackageIds.length ? `Vytvořit (${selectedPackageIds.length})` : 'Vytvořit'}>
                {selectedPackageIds.length ? `Vytvořit (${selectedPackageIds.length})` : 'Vytvořit'}
              </Button>
            ) : (
              <Button
                variant="default"
                size="lg"
                icon={faLayerGroup}
                onClick={() => handleBulkCreate(packages || [])}
                disabled={isQueryFetching || isLoading || isBulkCreating || selectedPackageIds.length === 0}
                title="Vytvořit hromadný svoz">
                Vytvořit hromadný svoz
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Page Title */}
      <div className="bg-white flex justify-center">
        <div className="w-full max-w-[1400px] px-4 pb-4">
          <h2 className="text-2xl font-semibold">Vytvořit nový svoz</h2>
        </div>
      </div>

      {/* Content Section */}
      <div className="bg-white flex items-center flex-col pb-4">{renderMainContent()}</div>

      {/* Loading Overlay */}
      {isCreating && !isBulkModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-lg shadow-lg">
            <Loader size="fill-container" />
            <div className="mt-2 text-sm text-gray-600">Zpracování...</div>
          </div>
        </div>
      )}

      {/* Bulk Disposal Modal */}
      <BulkDisposalModal
        isOpen={isBulkModalOpen}
        isCreating={isBulkCreating}
        progress={bulkProgress}
        result={bulkResult}
      />
    </div>
  )
}

export default DisposalCreate
